import React, { useContext, useMemo, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, MessagesSquare, Pencil, User, FileText } from "lucide-react";

import { Context as AuthContext } from "context/Auth";
import { Context as IntlContext } from "context/Intl";
import { useLoginDialog } from "context/LoginDialog";
import { useProfileChoiceDialog } from "context/ProfileChoiceDialog";
import { NotificationContext } from "app/Notification/Context";
import { countUnreadConversations } from "helpers/messaging/countUnreadConversations";
import { Role } from "types/user";
import logoPrimaryHover from "assets/images/logo/logo2025-primary_hover.svg";

import { Button } from "components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "components/ui/avatar";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/tooltip";
import { useFileObjectUrl } from "hooks/file";
import { cn } from "lib/utils";
import { getRoleSwitchNavigationTarget } from "helpers/navigation/roleSwitchPath";
import { ROUTES } from "routes";
import { ServicesMenuDesktop, ServicesMenuMobile } from "./ServicesMenu";

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
        "relative rounded-md px-2 py-2 text-xs font-bold uppercase tracking-wide transition-colors sm:px-3",
        isActive
            ? "text-primary after:absolute after:bottom-0 after:left-1/2 after:h-1.5 after:w-1.5 after:-translate-x-1/2 after:rounded-full after:bg-primary"
            : "text-foreground/80 hover:text-foreground",
    );

const mobileNavLinkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
        "rounded-md py-3 pl-3 pr-3 text-sm font-bold uppercase transition-colors",
        isActive ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent",
    );

const UnreadCountBadge: React.FC<{ count: number; className?: string }> = ({ count, className }) => {
    if (count <= 0) {
        return null;
    }

    return (
        <span
            aria-hidden
            className={cn(
                "inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground",
                className,
            )}
        >
            {count}
        </span>
    );
};

const MessagingHeaderLink: React.FC<{
    unreadCount: number;
    unreadLabel: string | null;
    messagesLabel: string;
    isActive: boolean;
}> = ({ unreadCount, unreadLabel, messagesLabel, isActive }) => {
    const ariaLabel = unreadLabel ? `${messagesLabel}, ${unreadLabel}` : messagesLabel;

    return (
        <TooltipProvider delayDuration={100}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        asChild
                        variant="ghost"
                        size="icon"
                        className={cn(
                            "relative overflow-visible rounded-full [&_svg]:size-5",
                            isActive && "bg-accent text-primary",
                        )}
                    >
                        <Link to={ROUTES.MESSAGING} aria-label={ariaLabel}>
                            <MessagesSquare />
                            <UnreadCountBadge
                                count={unreadCount}
                                className="absolute -right-0.5 -top-0.5 ring-2 ring-background"
                            />
                        </Link>
                    </Button>
                </TooltipTrigger>
                <TooltipContent>{messagesLabel}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

const Topbar: React.FC = () => {
    const { isLoggedIn, logout, account, currentRole, selectRole } = useContext(AuthContext);
    const intl = useContext(IntlContext);
    const { threads } = useContext(NotificationContext);
    const location = useLocation();
    const isServicesActive = location.pathname.startsWith(ROUTES.SERVICES + "/");
    const {
        actions: { openLogin },
    } = useLoginDialog();
    const { openProfileChoice } = useProfileChoiceDialog();
    const [sheetOpen, setSheetOpen] = useState(false);

    const unreadConversationCount = useMemo(() => countUnreadConversations(threads), [threads]);
    const unreadConversationsLabel =
        unreadConversationCount > 0
            ? intl.formatMessage("menu.unread_conversations_badge", { count: unreadConversationCount })
            : null;
    const isMessagingActive = location.pathname.startsWith(ROUTES.MESSAGING);

    const roleLabel = useMemo(() => {
        if (currentRole === Role.PROVIDER) return intl.formatMessage("menu.header_role_provider");
        if (currentRole === Role.CLIENT) return intl.formatMessage("menu.header_role_client");
        return currentRole || "";
    }, [currentRole, intl]);

    const profileTo = currentRole === Role.PROVIDER ? ROUTES.ACCOUNT_PROFILE : ROUTES.ACCOUNT;
    const myListTo = currentRole === Role.PROVIDER ? ROUTES.MY_SERVICES : ROUTES.MY_ADS;
    const myListLabelId =
        currentRole === Role.PROVIDER ? "profile.nav.my_services" : "profile.nav.my_ads";

    const userPictureSrc = useFileObjectUrl(account?.user.picture_id);
    const postAdHref = ROUTES.MY_ADS_CREATE;

    const changeRole = async (role: Role) => {
        const target =
            getRoleSwitchNavigationTarget(location.pathname, role) ??
            `${location.pathname}${location.search}`;
        await selectRole(role, target);
    };

    return (
        <header className="sticky top-0 z-50 w-full border-b border-border/60 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
            <div className="mx-auto flex h-16 w-full max-w-[1400px] items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">
                <div className="flex min-w-0 flex-1 items-center gap-3 lg:gap-8">
                    <Link
                        className="inline-flex shrink-0 items-center"
                        to={ROUTES.HOME}
                        title={intl.formatMessage("menu.home")}
                    >
                        <img src={logoPrimaryHover} alt="Twoghether" className="h-8 w-auto sm:h-9" />
                    </Link>

                    <nav
                        className="hidden min-w-0 flex-1 items-center justify-center gap-0.5 md:flex lg:gap-1"
                        aria-label="Main"
                    >
                        <NavLink to={ROUTES.HOME} className={navLinkClass} end>
                            {intl.formatMessage("menu.welcome")}
                        </NavLink>
                        <ServicesMenuDesktop
                            label={intl.formatMessage("menu.our_services")}
                            isActive={isServicesActive}
                        />
                        <NavLink to={ROUTES.PROVIDERS} className={navLinkClass}>
                            {intl.formatMessage("menu.providers_nav")}
                        </NavLink>
                        <NavLink to={ROUTES.ADS} className={navLinkClass}>
                            {intl.formatMessage("menu.ads")}
                        </NavLink>
                        <NavLink to={ROUTES.CONTACT} className={navLinkClass}>
                            {intl.formatMessage("menu.contact")}
                        </NavLink>
                        <NavLink to={ROUTES.ABOUT} className={navLinkClass}>
                            {intl.formatMessage("menu.about")}
                        </NavLink>
                    </nav>
                </div>

                <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
                    {!isLoggedIn && (
                        <Button
                            variant="default"
                            size="sm"
                            className="hidden sm:inline-flex"
                            onClick={() => openProfileChoice()}
                        >
                            {intl.formatMessage("menu.post_ad")}
                        </Button>
                    )}

                    {!isLoggedIn ? (
                        <Button
                            variant="outline"
                            size="sm"
                            className="hidden sm:inline-flex"
                            onClick={() => openLogin()}
                        >
                            {intl.formatMessage("menu.login")}
                        </Button>
                    ) : (
                        <div className="flex items-center gap-1 sm:ml-4 sm:gap-2">
                            <MessagingHeaderLink
                                unreadCount={unreadConversationCount}
                                unreadLabel={unreadConversationsLabel}
                                messagesLabel={intl.formatMessage("menu.messages")}
                                isActive={isMessagingActive}
                            />
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        className="hidden h-auto gap-2 rounded-full px-2 py-1.5 hover:bg-accent sm:inline-flex"
                                    >
                                        <span className="hidden min-w-0 flex-col items-end text-left md:flex">
                                            <span className="max-w-[9rem] truncate text-sm font-bold leading-tight text-foreground">
                                                {account?.user.first_name}
                                            </span>
                                            <span className="max-w-[9rem] truncate text-xs text-muted-foreground">
                                                {roleLabel}
                                            </span>
                                        </span>
                                        <Avatar className="h-9 w-9 shrink-0 border-2 border-primary/20">
                                            {userPictureSrc && (
                                                <AvatarImage src={userPictureSrc} alt="" className="object-cover" />
                                            )}
                                            <AvatarFallback className="text-xs font-semibold">
                                                {account?.user.first_name?.[0]?.toUpperCase() ?? "?"}
                                            </AvatarFallback>
                                        </Avatar>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <DropdownMenuItem asChild>
                                        <Link to={profileTo} className="flex items-center gap-2">
                                            <User className="h-4 w-4" />
                                            {intl.formatMessage("menu.my_account")}
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to={myListTo} className="flex items-center gap-2">
                                            <FileText className="h-4 w-4" />
                                            {intl.formatMessage(myListLabelId)}
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    {account?.user.roles.includes(Role.PROVIDER) &&
                                        account?.user.roles.includes(Role.CLIENT) && (
                                            <>
                                                {currentRole === Role.PROVIDER ? (
                                                    <DropdownMenuItem onClick={() => changeRole(Role.CLIENT)}>
                                                        {intl.formatMessage("menu.client_mode")}
                                                    </DropdownMenuItem>
                                                ) : (
                                                    <DropdownMenuItem onClick={() => changeRole(Role.PROVIDER)}>
                                                        {intl.formatMessage("menu.provider_mode")}
                                                    </DropdownMenuItem>
                                                )}
                                                <DropdownMenuSeparator />
                                            </>
                                        )}
                                    <DropdownMenuItem onClick={() => void logout()}>
                                        {intl.formatMessage("authentication.login.action.logout")}
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    )}

                    <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                        <SheetTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="shrink-0 md:hidden"
                                aria-label={intl.formatMessage("menu.open")}
                            >
                                <Menu className="h-5 w-5" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="right" className="flex h-full w-full flex-col overflow-hidden sm:max-w-sm">
                            <SheetHeader className="shrink-0">
                                <SheetTitle>Menu</SheetTitle>
                            </SheetHeader>
                            {isLoggedIn && account && (
                                <div className="mt-4 flex shrink-0 items-center gap-3 rounded-lg border border-border/60 bg-muted/30 p-3">
                                    <Avatar className="h-12 w-12 shrink-0 border-2 border-primary/20">
                                        {userPictureSrc && (
                                            <AvatarImage src={userPictureSrc} alt="" className="object-cover" />
                                        )}
                                        <AvatarFallback className="text-sm font-semibold">
                                            {account.user.first_name?.[0]?.toUpperCase() ?? "?"}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate font-bold text-foreground">{account.user.first_name}</p>
                                        <p className="truncate text-xs text-muted-foreground">{roleLabel}</p>
                                    </div>
                                </div>
                            )}
                            <div className="mt-4 flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain">
                                <nav className="flex flex-col gap-1" aria-label="Mobile">
                                    <NavLink
                                        to={ROUTES.HOME}
                                        className={mobileNavLinkClass}
                                        end
                                        onClick={() => setSheetOpen(false)}
                                    >
                                        {intl.formatMessage("menu.welcome")}
                                    </NavLink>
                                    <ServicesMenuMobile
                                        label={intl.formatMessage("menu.our_services")}
                                        isActive={isServicesActive}
                                        onClose={() => setSheetOpen(false)}
                                    />
                                    <NavLink
                                        to={ROUTES.PROVIDERS}
                                        className={mobileNavLinkClass}
                                        onClick={() => setSheetOpen(false)}
                                    >
                                        {intl.formatMessage("menu.providers_nav")}
                                    </NavLink>
                                    <NavLink
                                        to={ROUTES.ADS}
                                        className={mobileNavLinkClass}
                                        onClick={() => setSheetOpen(false)}
                                    >
                                        {intl.formatMessage("menu.ads")}
                                    </NavLink>
                                    <NavLink
                                        to={ROUTES.ABOUT}
                                        className={mobileNavLinkClass}
                                        onClick={() => setSheetOpen(false)}
                                    >
                                        {intl.formatMessage("menu.about")}
                                    </NavLink>
                                </nav>
                                <div className="mt-6 flex shrink-0 flex-col gap-2 border-t pt-4 pb-2">
                                    {!isLoggedIn ? (
                                        <>
                                            <Button
                                                variant="default"
                                                size="sm"
                                                onClick={() => {
                                                    setSheetOpen(false);
                                                    openProfileChoice();
                                                }}
                                            >
                                                <Pencil className="h-4 w-4" aria-hidden />
                                                {intl.formatMessage("menu.post_ad")}
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                    setSheetOpen(false);
                                                    openLogin();
                                                }}
                                            >
                                                {intl.formatMessage("menu.login")}
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <Button asChild variant="default" size="sm">
                                                <Link to={postAdHref} onClick={() => setSheetOpen(false)}>
                                                    <Pencil className="h-4 w-4" aria-hidden />
                                                    {intl.formatMessage("menu.post_ad")}
                                                </Link>
                                            </Button>
                                            <Button asChild variant="outline" size="sm">
                                                <Link to={profileTo} onClick={() => setSheetOpen(false)}>
                                                    {intl.formatMessage("menu.my_account")}
                                                </Link>
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                className="w-full"
                                                onClick={() => {
                                                    setSheetOpen(false);
                                                    void logout();
                                                }}
                                            >
                                                {intl.formatMessage("authentication.login.action.logout")}
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
};

export default Topbar;
